import { Suspense, lazy, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@/lib/zodResolver';
import { z } from 'zod';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { AuthLayout } from '@/components/templates/AuthLayout';
import { useAuth } from '@/hooks/useAuth';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { isMockApiEnabled } from '@/mocks/env';
import { homePath, paths } from '@/routes/paths';
import { isSafeInternalPath } from '@/lib/safePath';
import { isMfaChallenge } from '@/types';
import type { LoginResponse, MfaChallengeResponse } from '@/types';
import { MfaChallengeForm } from './MfaChallengeForm';

const DemoCredentialsPanel = lazy(() => import('@/mocks/DemoCredentialsPanel'));

const loginSchema = z.object({
  email: z.string().min(1, 'L’indirizzo e-mail è obbligatorio.').email('Inserisci un indirizzo e-mail valido.'),
  password: z.string().min(1, 'La password è obbligatoria.'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

interface LocationState {
  from?: { pathname: string };
}

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const { isDesktop } = useBreakpoint();
  const location = useLocation();
  const { login, completeLogin, error, errorCode, clearError, isAuthenticated, ruolo } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [challenge, setChallenge] = useState<MfaChallengeResponse | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [pendingSession, setPendingSession] = useState<LoginResponse | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const from = (location.state as LocationState | undefined)?.from?.pathname;
  const redirectTo = isSafeInternalPath(from) && from !== paths.dashboard ? from : homePath(ruolo);
  const pendingActivation = errorCode === 'ACCOUNT_PENDING_ACTIVATION';
  const emailUnverified = errorCode === 'EMAIL_NOT_VERIFIED';
  const accountDisabled = errorCode === 'ACCOUNT_DISABLED';
  const accountLocked = errorCode === 'ACCOUNT_LOCKED';

  useEffect(() => {
    if (isAuthenticated && !backupCodes) void navigate(redirectTo, { replace: true });
  }, [isAuthenticated, backupCodes, navigate, redirectTo]);

  const finish = async (session: LoginResponse): Promise<void> => {
    if (session.backupCodes && session.backupCodes.length > 0) {
      setBackupCodes(session.backupCodes);
      setPendingSession(session);
      setChallenge(null);
      return;
    }
    const user = await completeLogin(session);
    const dest = isSafeInternalPath(from) && from !== paths.dashboard ? from : homePath(user.ruolo);
    void navigate(dest, { replace: true });
  };

  const onSubmit = async (values: LoginFormValues): Promise<void> => {
    clearError();
    try {
      const result = await login(values);
      if (isMfaChallenge(result)) {
        setChallenge(result);
        return;
      }
      await finish(result);
    } catch {
      // Store holds message + code; the alert / dialog below render them.
    }
  };

  const signInWithDemoAccount = (credentials: LoginFormValues): Promise<void> => {
    setValue('email', credentials.email);
    setValue('password', credentials.password);
    return handleSubmit(onSubmit)();
  };

  if (backupCodes) {
    return (
      <AuthLayout
        title="Salva i codici di backup"
        subtitle="Ogni codice si usa una sola volta se perdi l’app autenticatore. Conservali in un posto sicuro."
      >
        <Stack spacing={4}>
          <Box
            component="ul"
            sx={{
              m: 0,
              p: 3,
              listStyle: 'none',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              bgcolor: 'action.hover',
              borderRadius: 1,
            }}
          >
            {backupCodes.map((code) => (
              <Typography component="li" key={code} variant="body2">
                {code}
              </Typography>
            ))}
          </Box>
          <Button
            variant="contained"
            size="large"
            onClick={() => {
              void (async () => {
                if (!pendingSession) return;
                const user = await completeLogin(pendingSession);
                const dest = isSafeInternalPath(from) && from !== paths.dashboard ? from : homePath(user.ruolo);
                setBackupCodes(null);
                setPendingSession(null);
                void navigate(dest, { replace: true });
              })();
            }}
          >
            Ho salvato i codici
          </Button>
        </Stack>
      </AuthLayout>
    );
  }

  if (challenge) {
    return (
      <AuthLayout
        title={challenge.status === 'mfa_enrollment' ? 'Attiva l’autenticazione a due fattori' : 'Verifica in due passaggi'}
        subtitle="Protezione dell’account con TOTP, come richiesto per l’accesso."
      >
        <MfaChallengeForm
          challenge={challenge}
          onCompleted={(session) => void finish(session)}
          onCancel={() => setChallenge(null)}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Accedi al tuo hub" subtitle="Inserisci le credenziali per gestire i tuoi servizi di sicurezza.">
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={4}>
          {error && !pendingActivation && (
            <Alert severity={emailUnverified || accountLocked ? 'warning' : 'error'} onClose={clearError}>
              {emailUnverified
                ? 'Verifica la tua email prima di accedere.'
                : accountDisabled
                  ? 'Account disattivato. L’azienda non è attualmente attiva.'
                  : accountLocked
                    ? 'Account sospeso temporaneamente per troppi tentativi di accesso. Riprova tra 15 minuti o contatta l’assistenza.'
                    : error}
            </Alert>
          )}

          <TextField
            {...register('email')}
            label="E-mail"
            type="email"
            autoComplete="email"
            autoFocus
            required
            error={Boolean(errors.email)}
            helperText={errors.email?.message}
            size="medium"
          />

          <TextField
            {...register('password')}
            label="Password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            required
            error={Boolean(errors.password)}
            helperText={errors.password?.message}
            size="medium"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((value) => !value)}
                    aria-label={showPassword ? 'Nascondi la password' : 'Mostra la password'}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? (
                      <VisibilityOffOutlinedIcon fontSize="small" />
                    ) : (
                      <VisibilityOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Link component={RouterLink} to={paths.forgotPassword} variant="caption">
              Password dimenticata?
            </Link>
          </Box>

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {isSubmitting ? 'Accesso in corso…' : 'Accedi'}
          </Button>

          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            Sei un operatore di un’azienda già cliente?{' '}
            <Link component={RouterLink} to={paths.registrazione} variant="body2">
              Registrati
            </Link>
          </Typography>

          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
            Accedendo accetti le condizioni di utilizzo del servizio.
          </Typography>

          {isMockApiEnabled() && (
            <Suspense fallback={null}>
              <DemoCredentialsPanel
                onSelect={(credentials) => void signInWithDemoAccount(credentials)}
                disabled={isSubmitting}
              />
            </Suspense>
          )}
        </Stack>
      </Box>

      <Dialog
        open={pendingActivation}
        onClose={clearError}
        fullScreen={!isDesktop}
        aria-labelledby="pending-activation-title"
      >
        <DialogTitle id="pending-activation-title">Account in attesa di attivazione</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Il tuo account è in attesa di attivazione. Ti contatteremo presto.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearError} autoFocus>
            Ho capito
          </Button>
        </DialogActions>
      </Dialog>
    </AuthLayout>
  );
}
